import { useRouteNavigator } from '@vkontakte/vk-mini-apps-router';
import type { NavIdProps } from '@vkontakte/vkui';
import { useState, type ReactNode } from 'react';
import {
  cabinetApi,
  formatDate,
  type AbilityBody,
  type AbilityView,
  type PropertyState,
} from '../../api/cabinet';
import { errorMessage } from '../../api/client';
import { useLoader } from '../../api/loader';
import { usePagedLoader } from '../../api/paging';
import {
  ABILITY_KIND_LABELS,
  ABILITY_ORIGIN_LABELS,
  type FormAbilityEntry,
  type StartingAbility,
} from '../../api/types';
import { useBack } from '../../app/useBack';
import { t } from '../../i18n';
import { ClockIcon, LinkIcon, PlusIcon } from '../../shared/icons';
import { versionLabel } from '../../shared/lib/docNumber';
import { useSearchTerm } from '../../shared/lib/search';
import {
  Action,
  CardList,
  EmptyState,
  ErrorState,
  Gap,
  LoadingState,
  Media,
  Muted,
  NoticeBlock,
  PropertyCard,
  Screen,
  Section,
  SectionTitle,
  SourceLink,
  Tag,
  toast,
} from '../../shared/ui';
import {
  describeProperty,
  isLivePublished,
  PropertyActions,
  PropertyModeSwitch,
  PropertySearch,
  ReviewNote,
  type PropertyStatus,
} from '../property/propertyKit';
import { ShowMore } from '../review/staffKit';
import { AbilityForm, EMPTY_ABILITY, type AbilityFormValues } from './AbilityForm';

/**
 * Способности персонажа одним списком.
 *
 * Хранятся они в двух местах, и это правильно по природе данных: стартовые —
 * девятым пунктом версионируемой анкеты, выданные — строками таблицы
 * abilities с историей версий. А видеть игрок должен один список, поэтому
 * экран сводит их сам: сначала стартовые из ОПУБЛИКОВАННОЙ анкеты, следом
 * выданные.
 *
 * У стартовых здесь нет кнопок правки и удаления, и это не забывчивость:
 * они часть анкеты, правятся девятым пунктом и уезжают на модерацию вместе
 * с ней. Вместо них в карточке стоит «Править в анкете» — чтобы не пришлось
 * искать это место наугад.
 *
 * Замолчать выданная способность может двумя разными способами, и путать их
 * нельзя: срок вышел сам (expired, считается на чтении — статуса в базе нет)
 * или её отозвал квест-мастер (deletedAt, архив). Поэтому и разделы разные.
 */

const SECTION_TITLES: Record<PropertyState, string> = {
  published: t('ability.section.published'),
  requests: t('ability.section.requests'),
  archived: t('ability.section.archived'),
};

const EMPTY_TITLES: Record<PropertyState, string> = {
  published: t('ability.empty.title.published'),
  requests: t('common.empty.noRequests'),
  archived: t('common.empty.emptyArchive'),
};

const EMPTY_TEXTS: Record<PropertyState, string> = {
  published: t('ability.empty.text.published'),
  requests: t('ability.empty.text.requests'),
  archived: t('ability.empty.text.archived'),
};

type Editing = { kind: 'none' } | { kind: 'create' } | { kind: 'edit'; abilityId: number };

export function AbilitiesPanel({ id }: NavIdProps) {
  const routeNavigator = useRouteNavigator();
  const back = useBack();
  const [state_, setState_] = useState<PropertyState>('published');
  const [editing, setEditing] = useState<Editing>({ kind: 'none' });
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<number | null>(null);

  const search = useSearchTerm();

  const state = usePagedLoader<AbilityView>(
    (params, signal) =>
      cabinetApi.abilities({ state: state_, search: search.term || undefined }, params, signal),
    [state_, search.term],
  );

  // Анкета читается отдельно, но список показываем только вместе с ней: иначе
  // он дорисовывался бы на глазах — сперва без стартовых, потом с ними.
  const character = useLoader(() => cabinetApi.character(), []);

  const ready = state.data !== null && character.data !== null;
  const error = state.error ?? character.error;
  const rows = state.data?.items ?? [];

  // Стартовые берём только из опубликованной версии: черновик и заявку ещё
  // никто не принял, а стартовая способность действует ровно столько, сколько
  // её версия анкеты висит в реестре.
  const starting = toStartingAbilities(character.data?.published?.abilities);

  const retry = () => {
    state.reload();
    character.reload();
  };

  const live = rows.filter((row) => !row.expired);
  const expired = rows.filter((row) => row.expired);

  const run = async (action: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    try {
      await action();
      if (done !== undefined) toast(done);
      setEditing({ kind: 'none' });
      setConfirmArchive(null);
      state.reload();
    } catch (cause) {
      toast(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const editingAbility =
    editing.kind === 'edit' ? (rows.find((row) => row.id === editing.abilityId) ?? null) : null;

  const cardProps = (ability: AbilityView) => ({
    ability,
    busy,
    confirming: confirmArchive === ability.id,
    onEdit: () => setEditing({ kind: 'edit', abilityId: ability.id }),
    onAskArchive: () => setConfirmArchive(ability.id),
    onCancelArchive: () => setConfirmArchive(null),
    onArchive: (reason: string) =>
      void run(() => cabinetApi.archiveAbility(ability.id, reason), t('ability.toast.archived')),
  });

  return (
    <Screen
      id={id}
      title={t('common.section.abilities')}
      back={back}
      count={state.data?.total}
      unit="ability"
    >
      {!ready && error === null ? <LoadingState /> : null}

      {!ready && error !== null ? (
        <ErrorState title={t('ability.error.load')} message={error} onRetry={retry} />
      ) : null}

      {editing.kind === 'create' ? (
        <Section>
          <ReviewNote>{t('ability.note.create')}</ReviewNote>
          <AbilityForm
            initial={EMPTY_ABILITY}
            submitLabel={t('ability.action.submit')}
            busy={busy}
            onCancel={() => setEditing({ kind: 'none' })}
            onSubmit={(values) =>
              void run(() => cabinetApi.createAbility(toBody(values)), t('ability.toast.created'))
            }
          />
        </Section>
      ) : null}

      {editingAbility !== null ? (
        <Section>
          <ReviewNote>{editNote(editingAbility)}</ReviewNote>

          <AbilityForm
            initial={toValues(editingAbility)}
            submitLabel={t('common.action.save')}
            busy={busy}
            onCancel={() => setEditing({ kind: 'none' })}
            onSubmit={(values) =>
              void run(
                () =>
                  cabinetApi.updateAbility(
                    editingAbility.id,
                    toBody(values, toValues(editingAbility)),
                  ),
                t('common.saved'),
              )
            }
          />
        </Section>
      ) : null}

      {editing.kind === 'none' && ready ? (
        <>
          {/* Раздел и поиск с кнопкой — одним блоком над списком. */}
          <Section>
            <PropertyModeSwitch state={state_} onChange={setState_} disabled={busy} />

            <PropertySearch
              value={search.input}
              onChange={search.setInput}
              placeholder={t('ability.search.placeholder')}
              action={
                state_ === 'published' ? (
                  <Action
                    tone="action"
                    size="m"
                    before={<PlusIcon />}
                    onClick={() => setEditing({ kind: 'create' })}
                  >
                    {t('ability.action.create')}
                  </Action>
                ) : null
              }
            />
          </Section>

          {/* Не пришли смена фильтра или дочитывание: на экране ещё прежние
              строки, и молчать об этом нельзя. */}
          {error !== null ? (
            <NoticeBlock
              tone="error"
              title={t('ability.error.load')}
              action={
                <Action tone="quiet" size="s" onClick={retry}>
                  {t('common.action.retry')}
                </Action>
              }
            >
              {error}
            </NoticeBlock>
          ) : null}

          {state_ === 'published' ? (
            <>
              <Section>
                <SectionTitle>{SECTION_TITLES.published}</SectionTitle>
                {starting.length === 0 && live.length === 0 && error === null ? (
                  <EmptyState title={EMPTY_TITLES.published}>{EMPTY_TEXTS.published}</EmptyState>
                ) : (
                  <CardList>
                    {starting.map(({ slot, ability }) => (
                      <StartingAbilityCard
                        key={slot}
                        ability={ability}
                        onOpenForm={() => routeNavigator.push('/me/form')}
                      />
                    ))}
                    {live.map((ability) => (
                      <AbilityCard key={ability.id} {...cardProps(ability)} />
                    ))}
                  </CardList>
                )}
              </Section>

              {expired.length > 0 ? (
                <Section>
                  <SectionTitle>{t('ability.section.expired')}</SectionTitle>
                  <CardList>
                    {expired.map((ability) => (
                      <AbilityCard key={ability.id} {...cardProps(ability)} />
                    ))}
                  </CardList>
                </Section>
              ) : null}
            </>
          ) : (
            <Section>
              <SectionTitle>{SECTION_TITLES[state_]}</SectionTitle>
              {rows.length === 0 && error === null ? (
                <EmptyState title={EMPTY_TITLES[state_]}>{EMPTY_TEXTS[state_]}</EmptyState>
              ) : (
                <CardList>
                  {rows.map((ability) => (
                    <AbilityCard key={ability.id} {...cardProps(ability)} />
                  ))}
                </CardList>
              )}
            </Section>
          )}

          <ShowMore page={state.data} onMore={state.loadMore} loading={state.loading} />
        </>
      ) : null}
    </Screen>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Карточки
   ────────────────────────────────────────────────────────────────────────── */

/**
 * Стартовая способность.
 *
 * Карточка та же, что у выданной, — различаются только данные: строки в
 * таблице у стартовой нет, поэтому нет ни вида, ни срока, ни кнопок правки и
 * архива. Вместо них одна кнопка в анкету: менять эту способность можно
 * только девятым пунктом, и уедет она на проверку вместе с анкетой.
 */
function StartingAbilityCard({
  ability,
  onOpenForm,
}: {
  ability: StartingAbility;
  onOpenForm: () => void;
}) {
  return (
    <PropertyCard
      image={null}
      kind="ability"
      title={ability.title || t('ability.empty.noTitle')}
      badge={ABILITY_ORIGIN_LABELS.form.toLocaleLowerCase('ru')}
      tone="archived"
      description={ability.description}
      extra={
        <Media aside={<ClockIcon tone="muted" />}>
          <Muted>{t('ability.starting.note')}</Muted>
        </Media>
      }
      actions={
        <Action tone="accent" size="m" stretched onClick={onOpenForm}>
          {t('ability.action.editInForm')}
        </Action>
      }
    />
  );
}

interface AbilityCardProps {
  ability: AbilityView;
  busy: boolean;
  confirming: boolean;
  onEdit: () => void;
  onAskArchive: () => void;
  onCancelArchive: () => void;
  onArchive: (reason: string) => void;
}

function AbilityCard({
  ability,
  busy,
  confirming,
  onEdit,
  onAskArchive,
  onCancelArchive,
  onArchive,
}: AbilityCardProps) {
  const status = describeAbility(ability);
  const archived = ability.deletedAt !== null;

  return (
    <PropertyCard
      image={ability.imageId === null ? null : { id: ability.imageId }}
      kind="ability"
      title={ability.title}
      badge={status.badge}
      tone={status.tone}
      // Вид — чип: в макете он единственный цветной знак в строке фактов и
      // отвечает на первый вопрос к карточке — способность это или дебаф.
      // Номер версии показываем со второй: у первой различать нечего.
      meta={[
        <Tag key="kind" tone={ability.kind === 'debuff' ? 'warning' : 'success'}>
          {ABILITY_KIND_LABELS[ability.kind]}
        </Tag>,
        ability.versionNo > 1 ? versionLabel(ability.versionNo) : null,
      ]}
      description={ability.description}
      hint={status.hint}
      extra={abilityExtra(ability)}
      actions={
        archived ? (
          <Muted>{t('ability.archived.note')}</Muted>
        ) : ability.expired ? undefined : (
          // У способности с вышедшим сроком кнопок нет: править нечего — срок
          // считается на чтении, и новая версия его не продлит, а архивировать
          // то, что и так не действует, незачем. В макете эта карточка тоже
          // стоит без полосы действий.
          <PropertyActions
            subjectAccusative={t('ability.subject')}
            busy={busy}
            confirming={confirming}
            onEdit={onEdit}
            onAskArchive={onAskArchive}
            onCancelArchive={onCancelArchive}
            onArchive={onArchive}
          />
        )
      }
    />
  );
}

/**
 * Источник выдачи. Поле свободное: туда пишут и адрес поста, и словами «с
 * зимнего квеста». Ссылкой делаем только настоящий адрес — относительный href
 * увёл бы в никуда, приложение живёт на хеш-роутере.
 */
/**
 * Хвост карточки: сначала срок, потом источник — тем же порядком, что в
 * макете. Срок отвечает «работает ли это сейчас», источник — «откуда это
 * взялось», и первый вопрос задают чаще.
 */
function abilityExtra(ability: AbilityView): ReactNode {
  const expiry = expiryLine(ability);
  const source = sourceExtra(ability.sourceLink);
  if (expiry === undefined && source === undefined) return undefined;

  return (
    <>
      {expiry}
      {expiry !== undefined && source !== undefined ? <Gap step="s2" /> : null}
      {source}
    </>
  );
}

/** Срок строкой со значком часов: у бессрочной его нет вовсе. */
function expiryLine(ability: AbilityView): ReactNode {
  if (ability.expiresAt === null) return undefined;

  return (
    <Media aside={<ClockIcon tone={ability.expired ? 'muted' : 'accent'} />}>
      <Muted>
        {ability.expired
          ? t('ability.expiry.past', { date: formatDate(ability.expiresAt) })
          : t('ability.expiry.future', { date: formatDate(ability.expiresAt) })}
      </Muted>
    </Media>
  );
}

function sourceExtra(link: string | null): ReactNode {
  const value = link?.trim() ?? '';
  if (value === '') return undefined;

  // Значок вместо слова «Источник»: подпись занимала треть строки, а адрес —
  // одно длинное слово, и вместе они переносились посреди ссылки.
  return (
    <Media aside={<LinkIcon tone="accent" />}>
      <SourceLink href={value}>{value}</SourceLink>
    </Media>
  );
}

/**
 * Состояние способности. Отличие от общего describeProperty ровно одно: срок.
 * Он считается на чтении и к модерации отношения не имеет, поэтому и разбирают
 * его здесь, а не в общем наборе.
 */
function describeAbility(ability: AbilityView): PropertyStatus {
  // Свои формулировки только у действующей: у неё есть срок, которого нет у вещей.
  if (isLivePublished(ability)) {
    if (ability.expired) {
      // Истёкшая и отозванная — разные состояния, и различает их подпись:
      // оттенок у обеих один, пятого цвета в палитре нет.
      return {
        badge: t('ability.state.expired'),
        // Срок печатает строка со значком часов — плашка повторила бы его
        // слово в слово. Чем истёкшая отличается от отозванной, говорят
        // значок, подпись раздела и пояснение под ним.
        hint: null,
        tone: 'archived',
      };
    }
    return {
      // Та же подпись, что у предмета и питомца: «в карточке» — это про
      // состояние вещи, и своего слова у способности здесь нет. Что у неё
      // есть срок, говорит строка со значком часов, а не бейдж.
      badge: t('common.state.published'),
      hint: null,
      tone: 'published',
    };
  }

  return describeProperty(ability);
}

/* ──────────────────────────────────────────────────────────────────────────
   Версия ↔ форма
   ────────────────────────────────────────────────────────────────────────── */

/**
 * Девятый пункт анкеты — в члены общего списка.
 *
 * Пустые заготовки отбрасываем: редактор анкеты умеет сохранить строку без
 * названия и описания, и выдавать её за способность незачем.
 *
 * `slot` — номер пункта в анкете: другого имени у стартовой способности нет.
 */
function toStartingAbilities(
  entries: FormAbilityEntry[] | undefined,
): { slot: number; ability: StartingAbility }[] {
  if (entries === undefined) return [];

  return entries
    .map((entry, slot) => ({ entry, slot }))
    .filter(({ entry }) => entry.title.trim() !== '' || (entry.description ?? '').trim() !== '')
    .map(({ entry, slot }) => ({
      slot,
      ability: { origin: 'form', title: entry.title, description: entry.description },
    }));
}

function editNote(ability: AbilityView): string {
  if (ability.status === 'rejected') {
    return t('ability.note.edit.rejected');
  }
  if (ability.status !== 'approved') {
    return t('ability.note.edit.pending');
  }
  return t('ability.note.edit.approved');
}

function toValues(ability: AbilityView): AbilityFormValues {
  return {
    kind: ability.kind,
    title: ability.title,
    description: ability.description ?? '',
    sourceLink: ability.sourceLink ?? '',
    expiresAt: ability.expiresAt === null ? '' : ability.expiresAt.slice(0, 10),
    imageId: ability.imageId,
  };
}

/**
 * initial передают на правке: срок уходит на сервер, только если его меняли.
 * Иначе истёкшую способность стало бы не отредактировать вовсе — сервер требует,
 * чтобы присланный срок был будущим, и отказ приходил бы про поле, которое не трогали.
 */
function toBody(values: AbilityFormValues, initial?: AbilityFormValues): AbilityBody {
  const body: AbilityBody = {
    kind: values.kind,
    title: values.title,
    description: values.description,
    sourceLink: values.sourceLink,
  };

  if (initial === undefined || values.imageId !== initial.imageId) {
    body.imageId = values.imageId;
  }

  if (initial === undefined || values.expiresAt !== initial.expiresAt) {
    // Пустая строка не пройдёт как ISO 8601: «бессрочно» — это null.
    body.expiresAt = values.expiresAt === '' ? null : values.expiresAt;
  }

  return body;
}
